/**
 * Sidebar management module
 * Handles sidebar state persistence and observation
 */

import { CONFIG } from '../core/config.js';
import eventBus, { EVENTS } from '../core/event-bus.js';

/**
 * Sets up a MutationObserver to monitor sidebar collapse state changes
 * Automatically saves/removes sidebar state in cookies when the sidebar is toggled
 *
 * @function setupSidebarObserver
 * @description Creates a MutationObserver that watches for class changes on the body element.
 * When the 'sidebar-collapse' class is added or removed, it automatically updates
 * the corresponding cookie to persist the sidebar state across page reloads.
 *
 * The observer:
 * - Monitors only class attribute changes on document.body
 * - Detects 'sidebar-collapse' class presence
 * - Saves state to configured cookie when collapsed
 * - Removes cookie when sidebar is expanded
 * - Uses dynamic imports to avoid circular dependencies
 * - Emits events for other modules to listen to
 *
 * @example
 * // Call this function during application initialization
 * setupSidebarObserver();
 */
export function setupSidebarObserver() {
    // Create a MutationObserver to watch for class changes on body element
    const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                const isCollapsed = document.body.classList.contains('sidebar-collapse');

                // Import cookie functions dynamically to avoid circular dependency
                import('./cookies.js').then(module => {
                    if (isCollapsed) {
                        module.setCookie(CONFIG.COOKIES.SIDEBAR_STATE, true);
                    } else {
                        module.deleteCookie(CONFIG.COOKIES.SIDEBAR_STATE);
                    }
                });

                // Emit event for other modules
                eventBus.emit(EVENTS.SIDEBAR_TOGGLED, { collapsed: isCollapsed });
            }
        });
    });

    // Start observing the body element for attribute changes
    observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
    });
}
